import React from 'react';
import { Mui, MuiIcons } from '@osu/react-ui';
import { useGetScholarsPreferencesQuery, useGetOpenScholarsProgramsQuery } from '../../../services/graphql/api';
import useUserEmplid from '../../hooks/useUserEmplid';

/**
 * has505 — Read-only display of a student's submitted Scholars program preferences.
 * Shown on the dashboard after preference submission and during the review period.
 * Owner-scoped: fetches the logged-in student's own preferences.
 */
export default function ScholarsPreferencesReadOnly() {
    const { emplid } = useUserEmplid();
    const { data: preferences, isLoading, isError } = useGetScholarsPreferencesQuery(
        { applicantEmployeeId: emplid },
        { skip: !emplid }
    );
    // Fetch program list for label resolution
    const { data: programs } = useGetOpenScholarsProgramsQuery();

    if (isLoading) {
        return (
            <Mui.Box sx={{ mt: 2 }}>
                <Mui.Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
            </Mui.Box>
        );
    }

    if (isError || !preferences || preferences.length === 0) {
        return null; // Silently hide if no preferences found (shouldn't happen at this stage)
    }

    // Build label lookup from open programs
    const programLabels = (programs ?? []).reduce((acc, p) => {
        acc[p.subProgramId] = p.label || p.subProgramId;
        return acc;
    }, {});

    const sorted = [...preferences].sort((a, b) => a.rank - b.rank);

    // has571 — the backend stamps alternateOptIn (from the Application record) onto every returned
    // preference item, so read it off the first one. Coerce to a boolean so a missing/undefined value
    // renders as an explicit "not opted in" rather than a blank.
    const alternateOptIn = !!sorted[0]?.alternateOptIn;

    return (
        <Mui.Box sx={{ mt: 3 }}>
            <Mui.Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Your Program Preferences
            </Mui.Typography>
            <Mui.Paper variant="outlined" sx={{ p: 2 }}>
                <Mui.List dense disablePadding>
                    {sorted.map((pref) => (
                        <Mui.ListItem key={pref.rank} disableGutters sx={{ py: 0.5 }}>
                            <Mui.ListItemText
                                primary={`${pref.rank}. ${programLabels[pref.subProgramId] || pref.subProgramId}`}
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </Mui.ListItem>
                    ))}
                </Mui.List>

                {/* has571 — surface whether the student opted in to be considered for other (alternate)
                    programs if they aren't admitted to a ranked choice. Shown for both states so the
                    student can confirm what they submitted. */}
                <Mui.Divider sx={{ my: 1.5 }} />
                <Mui.Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    data-testid="scholars-alternate-opt-in-status"
                >
                    {alternateOptIn
                        ? <MuiIcons.CheckCircle fontSize="small" color="success" aria-hidden="true" />
                        : <MuiIcons.Cancel fontSize="small" color="disabled" aria-hidden="true" />}
                    <Mui.Typography variant="body2">
                        {alternateOptIn
                            ? 'You opted in to be considered for other programs if you are not admitted to a ranked choice.'
                            : 'You did not opt in to be considered for other programs.'}
                    </Mui.Typography>
                </Mui.Box>
            </Mui.Paper>
        </Mui.Box>
    );
}
